import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { adminBlog, adminCreateBlog, adminUpdateBlog, adminDeleteBlog, apiError } from "../../lib/api";
import { Field, Input, Textarea, Btn, Modal, ImageUpload } from "./AdminKit";

const blank = { title: "", category: "News", date: new Date().toISOString().slice(0, 10), read: "3 min read", excerpt: "", image: "", body: "" };

export default function AdminNews() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  const load = () => adminBlog().then(setList).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...blank, ...p }); setOpen(true); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const { slug, id, ...payload } = form;
    try {
      if (editing) await adminUpdateBlog(editing.slug, payload);
      else await adminCreateBlog(payload);
      toast.success(editing ? "Post updated." : "Post created.");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await adminDeleteBlog(p.slug); toast.success("Post deleted."); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-bold text-2xl text-nead-navy">News</h1>
          <p className="mt-1 text-sm text-nead-navy/55">Write and manage the posts shown on the public News page.</p>
        </div>
        <Btn onClick={openNew} data-testid="news-add-btn"><Plus className="h-4 w-4" /> Add post</Btn>
      </div>

      <div className="mt-8 overflow-hidden rounded-2xl border border-black/[0.07] bg-white">
        <table className="w-full text-sm">
          <thead className="bg-nead-mist text-nead-navy/55 text-xs uppercase tracking-wider">
            <tr><th className="text-left px-5 py-3 font-semibold">Title</th><th className="text-left px-5 py-3 font-semibold">Category</th><th className="text-left px-5 py-3 font-semibold">Date</th><th className="px-5 py-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {list.map((p) => (
              <tr key={p.slug} data-testid={`news-row-${p.slug}`}>
                <td className="px-5 py-4 font-semibold text-nead-navy max-w-md">{p.title}</td>
                <td className="px-5 py-4 text-nead-navy/70">{p.category}</td>
                <td className="px-5 py-4 text-nead-navy/70">{p.date}</td>
                <td className="px-5 py-4 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(p)} data-testid={`news-edit-${p.slug}`} className="p-2 text-nead-navy/50 hover:text-nead-pink"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(p)} data-testid={`news-delete-${p.slug}`} className="p-2 text-nead-navy/50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={4} className="px-5 py-10 text-center text-nead-navy/40">No posts yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit post" : "Add post"} wide>
        <form onSubmit={save} className="space-y-4" data-testid="news-form">
          <Field label="Title"><Input required value={form.title} onChange={set("title")} data-testid="news-title" /></Field>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Category"><Input value={form.category} onChange={set("category")} data-testid="news-category" /></Field>
            <Field label="Date"><Input type="date" required value={form.date} onChange={set("date")} data-testid="news-date" /></Field>
            <Field label="Read time"><Input value={form.read} onChange={set("read")} /></Field>
          </div>
          <Field label="Image"><ImageUpload value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} testId="news-image" /></Field>
          <Field label="Excerpt"><Textarea rows={2} value={form.excerpt} onChange={set("excerpt")} data-testid="news-excerpt" /></Field>
          <Field label="Body"><Textarea rows={8} required value={form.body} onChange={set("body")} data-testid="news-body" placeholder="Use blank lines to separate paragraphs." /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <Btn type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn type="submit" disabled={busy} data-testid="news-save">{busy ? "Saving…" : "Save post"}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
